import { atom } from 'jotai'
import { atomFamily } from 'jotai/utils'
import keyBy from 'lodash/keyBy'
import mapValues from 'lodash/mapValues'
import sortBy from 'lodash/sortBy'
import defaults from 'lodash/defaults'

import { Grids } from 'WebAPI'
import { isEqual } from 'lodash'

import { getGridIdentity } from '../../../helpers'

import { saveGrid, viewGrid } from 'src/store/grid'

const COLUMN_WIDTH_DEFAULT = 150
const ICON_COLUMN_WIDTH = 30

type Attributes = {
  form: string
  object: string
  customID?: string | number
  sync: boolean
  iconColumns: string[]
  sample: any
}

interface Column {
  FieldName: string
  Pos: number
  Width: number
}

export type Entry = {
  sync: boolean
  columns: Column[]
  widths: Record<string, number>
  visibilities: Record<string, boolean>
  orders: string[]
  headers: Record<string, string>
  filters: Record<string, string>
  complete: boolean
}

const initialEntry = {
  sync: false,
  columns: [],
  filters: {},
  headers: {},
  orders: [],
  visibilities: {},
  widths: {},
  complete: true,
}

export const isGridEmpty = (entry: Entry) => entry.columns.length === 0 && entry.complete === true

const loadColumns = (
  columns: Column[],
  iconColumns: string[],
  columnFilters: Record<string, string> = {},
): Pick<Entry, 'columns' | 'headers' | 'orders' | 'visibilities' | 'widths' | 'filters'> => {
  const dictionary = keyBy(columns, 'FieldName')

  const visibilities = mapValues(dictionary, ({ Width }) => Width !== -1)

  const widths = mapValues(dictionary, ({ FieldName, Width }) => {
    if (iconColumns.includes(FieldName)) {
      return ICON_COLUMN_WIDTH
    }

    if (!visibilities[FieldName]) {
      return COLUMN_WIDTH_DEFAULT
    }

    return Width
  })

  const orders = sortBy(columns, 'Pos').map((col) => col.FieldName)
  const headers = mapValues(dictionary, 'FieldName')

  return {
    columns,
    headers,
    orders,
    visibilities,
    widths,
    filters: columnFilters,
  }
}

const loadSampleColumns = (sample: any = {}, iconColumns: string[]) => {
  const columns = Object.keys(sample)
    .filter((key: string) => !key.startsWith('HashSum_'))
    .map(
      (key): Column => ({
        FieldName: key,
        Pos: 0,
        Width: COLUMN_WIDTH_DEFAULT,
      }),
    )

  const columnFilters = mapValues(keyBy(columns, 'FieldName'), (column) =>
    typeof sample[column.FieldName] === 'number' ? 'agNumberColumnFilter' : 'agTextColumnFilter',
  )

  return loadColumns(columns, iconColumns, columnFilters)
}

const gridStatesAtom = atom<Record<string, Entry>>({})

export const gridStateAtomFamily = atomFamily((id: string) =>
  atom(
    (get) => get(gridStatesAtom)[id] || initialEntry,
    (get, set, entry: Entry) => {
      set(gridStatesAtom, (prev) => ({ ...prev, [id]: entry }))
    },
  ),
)

export const setGridStateByServerAtomWrapper = (dispatch) => {
  return atom(null, async (get, set, attributes: Attributes) => {
    const gridStateEntryAtom = gridStateAtomFamily(
      getGridIdentity(attributes.form, attributes.object, attributes.customID),
    )

    const { form, object, sync, iconColumns, sample } = defaults(attributes, {
      sync: false,
      iconColumns: [],
    })

    let isSync = sync || false

    let columns: Column[] = []

    if (sync) {
      // const { GridState } = await Grids.view(form, object)

      //UPDATED API REQUEST gridstate_view_api
      const { payload: GridState } = await dispatch(viewGrid({ formName: form, objectName: object }))

      if (GridState.length !== 0) {
        columns = GridState as Column[]
      } else {
        isSync = false
      }
    }

    const entry = columns.length !== 0 ? loadColumns(columns, iconColumns) : loadSampleColumns(sample, iconColumns)

    set(gridStateEntryAtom, {
      ...entry,
      complete: entry.columns.length !== 0 ? true : false,
      sync: isSync,
    })
  })
}

export const setGridStateBySampleAtom = atom<null, { id: string; iconColumns: string[]; sample: any }>(
  null,
  (get, set, { id, iconColumns, sample }) => {
    const gridStateAtom = gridStateAtomFamily(id)

    set(gridStateAtom, {
      ...get(gridStateAtom),
      ...loadSampleColumns(sample, iconColumns),
      complete: true,
    })
  },
)

export const saveGridStateAtomWrapper = (dispatch) => {
  return atom<null, string>(null, async (get, set, id) => {
    const gridStateAtom = gridStateAtomFamily(id)

    const gridState = get(gridStateAtom)

    if (!gridState || !gridState.sync) {
      return
    }

    const { columns, orders, visibilities, widths } = gridState

    const currentColumns = orders.map((col, index) => ({
      FieldName: col,
      Width: visibilities[col] ? widths[col] : -1,
      Pos: index,
    }))

    if (isEqual(currentColumns, columns)) {
      return
    }

    try {
      const [form, object] = id.split('-')
      // await Grids.save(form, object, JSON.stringify(currentColumns))
      //UPDATED API REQUEST gridstate_save_api
      await dispatch(
        saveGrid({
          formName: form,
          objectName: object,
          gridStates: currentColumns,
        }),
      )

      set(gridStateAtom, {
        ...gridState,
        columns: currentColumns,
      })
    } catch (err) {
      console.warn(err, "couldn't save grid state")
    }
  })
}
